/**
 * Type hygiene improvements and validation utilities
 * Provides runtime type checking and validation for better type safety
 */

import type { CreateOrderDto, OrderItemDto, DeliveryInfoDto, DietaryRequirementsDto } from '../dtos/OrderDto';
import type {
  OrderReadyToSubmit,
  DeliveryData,
  DietaryData,
  CartItemReadyToSubmit,
} from '@/components/order/order.types';
import type { ProductDocument, ProductVariantDocument } from '@/types/manage.types';

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Type guards use 'any' by design for safe runtime validation of unknown data

/**
 * Type guard utilities
 */
export class TypeGuards {
  /**
   * Type guard for CreateOrderDto
   */
  static isCreateOrderDto(obj: any): obj is CreateOrderDto {
    return (
      obj &&
      typeof obj === 'object' &&
      Array.isArray(obj.items) &&
      obj.items.every(TypeGuards.isOrderItemDto) &&
      TypeGuards.isDeliveryInfoDto(obj.deliveryInfo) &&
      TypeGuards.isDietaryRequirementsDto(obj.dietaryRequirements)
    );
  }

  /**
   * Type guard for OrderItemDto
   */
  static isOrderItemDto(obj: any): obj is OrderItemDto {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.variantId === 'string' &&
      typeof obj.productId === 'string' &&
      typeof obj.sku === 'string' &&
      typeof obj.productName === 'string' &&
      typeof obj.price === 'number' &&
      typeof obj.quantity === 'number' &&
      Array.isArray(obj.attributes) &&
      (obj.note === undefined || typeof obj.note === 'string')
    );
  }

  /**
   * Type guard for DeliveryInfoDto
   */
  static isDeliveryInfoDto(obj: any): obj is DeliveryInfoDto {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.fullName === 'string' &&
      typeof obj.phoneNumber === 'string' &&
      typeof obj.email === 'string' &&
      typeof obj.contactMethod === 'string' &&
      (obj.streetAddress === null || typeof obj.streetAddress === 'string') &&
      typeof obj.city === 'string' &&
      typeof obj.state === 'string' &&
      typeof obj.zipCode === 'string' &&
      typeof obj.deliveryDate === 'number' &&
      typeof obj.deliveryTime === 'string'
    );
  }

  /**
   * Type guard for DietaryRequirementsDto
   */
  static isDietaryRequirementsDto(obj: any): obj is DietaryRequirementsDto {
    return (
      obj &&
      typeof obj === 'object' &&
      (obj.vegetarian === undefined || typeof obj.vegetarian === 'boolean') &&
      (obj.vegan === undefined || typeof obj.vegan === 'boolean') &&
      (obj.glutenFree === undefined || typeof obj.glutenFree === 'boolean') &&
      (obj.dairyFree === undefined || typeof obj.dairyFree === 'boolean') &&
      (obj.nuts === undefined || typeof obj.nuts === 'boolean') &&
      (obj.shellfish === undefined || typeof obj.shellfish === 'boolean') &&
      (obj.eggs === undefined || typeof obj.eggs === 'boolean') &&
      (obj.soy === undefined || typeof obj.soy === 'boolean') &&
      (obj.additionalComments === undefined || typeof obj.additionalComments === 'string')
    );
  }

  /**
   * Type guard for OrderReadyToSubmit
   */
  static isOrderReadyToSubmit(obj: any): obj is OrderReadyToSubmit {
    return (
      obj &&
      typeof obj === 'object' &&
      Array.isArray(obj.cart) &&
      obj.cart.every(TypeGuards.isCartItemReadyToSubmit) &&
      TypeGuards.isDeliveryData(obj.deliveryData) &&
      TypeGuards.isDietaryData(obj.dietaryData)
    );
  }

  /**
   * Type guard for CartItemReadyToSubmit
   */
  static isCartItemReadyToSubmit(obj: any): obj is CartItemReadyToSubmit {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.variant_id === 'string' &&
      typeof obj.quantity === 'number' &&
      (obj.note === undefined || typeof obj.note === 'string')
    );
  }

  /**
   * Type guard for DeliveryData
   */
  static isDeliveryData(obj: any): obj is DeliveryData {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.fullName === 'string' &&
      typeof obj.phoneNumber === 'string' &&
      typeof obj.email === 'string' &&
      (obj.contactMethod === undefined || typeof obj.contactMethod === 'string') &&
      (obj.streetAddress === undefined || typeof obj.streetAddress === 'string') &&
      typeof obj.city === 'string' &&
      typeof obj.state === 'string' &&
      typeof obj.zipCode === 'string' &&
      (obj.deliveryDate === undefined || typeof obj.deliveryDate === 'number') &&
      (obj.deliveryTime === undefined || typeof obj.deliveryTime === 'string')
    );
  }

  /**
   * Type guard for DietaryData
   */
  static isDietaryData(obj: any): obj is DietaryData {
    return TypeGuards.isDietaryRequirementsDto(obj); // Same structure
  }

  /**
   * Type guard for ProductDocument
   */
  static isProductDocument(obj: any): obj is ProductDocument {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.id === 'string' &&
      typeof obj.name === 'string' &&
      typeof obj.category === 'string' &&
      (obj.description === undefined || typeof obj.description === 'string')
    );
  }

  /**
   * Type guard for ProductVariantDocument
   */
  static isProductVariantDocument(obj: any): obj is ProductVariantDocument {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.id === 'string' &&
      typeof obj.product_id === 'string' &&
      typeof obj.price === 'number' &&
      Array.isArray(obj.attribute_combination) &&
      (obj.sku === undefined || typeof obj.sku === 'string')
    );
  }
}

/**
 * Runtime validation utilities
 */
export class TypeValidator {
  /**
   * Validate and sanitize CreateOrderDto
   */
  static validateCreateOrderDto(obj: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!obj) {
      errors.push('CreateOrderDto is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!Array.isArray(obj.items)) {
      errors.push('items must be an array');
    } else if (obj.items.length === 0) {
      errors.push('items cannot be empty');
    } else {
      obj.items.forEach((item: any, index: number) => {
        if (!TypeGuards.isOrderItemDto(item)) {
          errors.push(`items[${index}] is not a valid OrderItemDto`);
        }
        if (item.quantity <= 0) {
          errors.push(`items[${index}].quantity must be greater than 0`);
        }
        if (item.price < 0) {
          errors.push(`items[${index}].price cannot be negative`);
        }
      });
    }

    if (!TypeGuards.isDeliveryInfoDto(obj.deliveryInfo)) {
      errors.push('deliveryInfo is not valid');
    } else {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(obj.deliveryInfo.email)) {
        errors.push('deliveryInfo.email is not a valid email address');
      }

      // Phone validation
      if (obj.deliveryInfo.phoneNumber.length < 10) {
        warnings.push('deliveryInfo.phoneNumber seems too short');
      }

      // Delivery date validation
      if (obj.deliveryInfo.deliveryDate < Date.now()) {
        errors.push('deliveryInfo.deliveryDate cannot be in the past');
      }
    }

    if (!TypeGuards.isDietaryRequirementsDto(obj.dietaryRequirements)) {
      errors.push('dietaryRequirements is not valid');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate and sanitize OrderReadyToSubmit
   */
  static validateOrderReadyToSubmit(obj: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!obj) {
      errors.push('OrderReadyToSubmit is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!Array.isArray(obj.cart)) {
      errors.push('cart must be an array');
    } else if (obj.cart.length === 0) {
      errors.push('cart cannot be empty');
    } else {
      obj.cart.forEach((item: any, index: number) => {
        if (!TypeGuards.isCartItemReadyToSubmit(item)) {
          errors.push(`cart[${index}] is not a valid CartItemReadyToSubmit`);
        }
        if (item.quantity <= 0) {
          errors.push(`cart[${index}].quantity must be greater than 0`);
        }
      });
    }

    if (!TypeGuards.isDeliveryData(obj.deliveryData)) {
      errors.push('deliveryData is not valid');
    } else {
      // Similar validations as DeliveryInfoDto
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(obj.deliveryData.email)) {
        errors.push('deliveryData.email is not a valid email address');
      }

      if (obj.deliveryData.deliveryDate && obj.deliveryData.deliveryDate < Date.now()) {
        warnings.push('deliveryData.deliveryDate is in the past');
      }
    }

    if (!TypeGuards.isDietaryData(obj.dietaryData)) {
      errors.push('dietaryData is not valid');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }
}

/**
 * Type sanitization utilities
 */
export class TypeSanitizer {
  /**
   * Sanitize and normalize email addresses
   */
  static sanitizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  /**
   * Sanitize phone numbers
   */
  static sanitizePhoneNumber(phone: string): string {
    return phone.replace(/\D/g, ''); // Remove all non-digit characters
  }

  /**
   * Sanitize string fields
   */
  static sanitizeString(str: string): string {
    return str.trim();
  }

  /**
   * Sanitize price values
   */
  static sanitizePrice(price: number): number {
    return Math.round(price * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Sanitize quantity values
   */
  static sanitizeQuantity(quantity: number): number {
    return Math.max(1, Math.floor(quantity)); // Ensure positive integer
  }

  /**
   * Comprehensive sanitization for CreateOrderDto
   */
  static sanitizeCreateOrderDto(dto: CreateOrderDto): CreateOrderDto {
    return {
      items: dto.items.map((item) => ({
        ...item,
        productName: TypeSanitizer.sanitizeString(item.productName),
        price: TypeSanitizer.sanitizePrice(item.price),
        quantity: TypeSanitizer.sanitizeQuantity(item.quantity),
        note: item.note ? TypeSanitizer.sanitizeString(item.note) : item.note,
      })),
      deliveryInfo: {
        ...dto.deliveryInfo,
        fullName: TypeSanitizer.sanitizeString(dto.deliveryInfo.fullName),
        email: TypeSanitizer.sanitizeEmail(dto.deliveryInfo.email),
        phoneNumber: TypeSanitizer.sanitizePhoneNumber(dto.deliveryInfo.phoneNumber),
        city: TypeSanitizer.sanitizeString(dto.deliveryInfo.city),
        state: TypeSanitizer.sanitizeString(dto.deliveryInfo.state),
        zipCode: TypeSanitizer.sanitizeString(dto.deliveryInfo.zipCode),
        streetAddress: dto.deliveryInfo.streetAddress
          ? TypeSanitizer.sanitizeString(dto.deliveryInfo.streetAddress)
          : dto.deliveryInfo.streetAddress,
      },
      dietaryRequirements: {
        ...dto.dietaryRequirements,
        additionalComments: dto.dietaryRequirements.additionalComments
          ? TypeSanitizer.sanitizeString(dto.dietaryRequirements.additionalComments)
          : dto.dietaryRequirements.additionalComments,
      },
    };
  }
}

/**
 * Export all utilities in a consolidated object
 */
export const TypeHygiene = {
  Guards: TypeGuards,
  Validator: TypeValidator,
  Sanitizer: TypeSanitizer,
};
